import { onlineManager, QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

// Since v5, tanstack query always assumes that the device is online: https://tanstack.com/query/latest/docs/framework/react/guides/migrating-to-v5#network-status-no-longer-relies-on-the-navigatoronline-property
// This breaks queries when the device is offline:
// 1. The queries are executed normally (although the device is offline).
// 2. After a few retries, the query is marked as failed and data in the cache is cleared.
// 3. This produces issues on next reload if the user is still offline (there's no data in the cache anymore).
//
// To handle this, we:
// 1. Set the online status to navigator.onLine initially.
// 2. Set networkMode to offlineFirst.
//    This still triggers one request independently of the network status (and potentially updates the cached data if successful).
//    But it stops on the first failing request and (more importantly) does not clear the cached data.
onlineManager.setOnline(navigator.onLine);

export const QUERY_CLIENT_CACHE_MAX_AGE = 1000 * 60 * 60 * 24; // 24 hours

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The main offline use case is to open an active booking without internet connection, and being able to open/close the bluetooth lock of the bike.
      // Bookings are usually not longer than 24 hours and the user needs to have an active connection to start/end the booking.
      // Therefore, we set the cache time to 24 hours.
      // This was introduced in the scope of https://linear.app/brainchimps/issue/BRA-112
      // Note that staleTime is 0 by default, so data is updated frequently if the user is online.
      gcTime: QUERY_CLIENT_CACHE_MAX_AGE,
      networkMode: "offlineFirst", // see comment above
      retry: (failureCount, error) => {
        if (
          isAxiosError(error) &&
          error.response?.status &&
          error.response.status < 500
        ) {
          // don't retry 4xx errors (like 401 Unauthorized or 404 Not Found),
          // as they usually cannot be fixed by retrying
          return false;
        }

        return failureCount < 3;
      },
    },
  },
});
